import { extensionOf } from "@/analytics/hygiene";
import type { FileExportKind } from "@/object-actions/fileRecordExportCatalog";
import { exportVariantsForContentKind } from "@/object-actions/fileRecordExportCatalog";
import { trackActionInvoked } from "@/object-actions/trackObjectAction";
import type {
  ObjectActionVariant,
  ResolvedObjectAction,
} from "@/object-actions/types";
import { classifyFileRecord } from "@/panes/file-record-detail/contentType";
import { trackFileDownloaded } from "@/utils/analytics";
import type { FileRecord } from "@cohesive/api-client";

export type FileRecordDownloadExpandParams = {
  record: FileRecord;
  runExport: (format: FileExportKind) => void | Promise<void>;
  isVariantDisabled?: (format: FileExportKind) => boolean;
};

/**
 * Expands the registry `download` action into format-specific variants
 * defined in {@link FILE_EXPORT_VARIANTS_BY_CONTENT_KIND}. Removes download
 * when the record is not downloadable (folder, unprocessed, etc.).
 */
export function attachFileRecordDownloadVariants(
  actions: ResolvedObjectAction[],
  params: FileRecordDownloadExpandParams | null,
): ResolvedObjectAction[] {
  if (
    !params ||
    params.record.status !== "processed" ||
    params.record.path?.endsWith("/")
  ) {
    return actions.filter((action) => action.id !== "download");
  }

  const entries = exportVariantsForContentKind(
    classifyFileRecord(params.record),
  );

  return actions.map((action) => {
    if (action.id !== "download") {
      return action;
    }

    const variants: ObjectActionVariant[] = entries.map((entry) => ({
      id: `download:${entry.format}`,
      label: entry.label,
      disabled: params.isVariantDisabled?.(entry.format) ?? false,
      handler: () => {
        trackActionInvoked("download", {
          objectKind: "file-record",
          objectSubtype: params.record.class,
          format: entry.format,
        });
        trackFileDownloaded({
          fileRecordId: params.record.id,
          format: entry.format,
          extension: extensionOf(params.record.path),
        });
        void params.runExport(entry.format);
      },
    }));

    if (variants.length === 1) {
      const only = variants[0];
      return {
        ...action,
        variants: undefined,
        handler: only.handler,
        disabled: only.disabled ?? action.disabled,
      };
    }

    return {
      ...action,
      variants,
      handler: () => {},
    };
  });
}
